let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  ActivityIndicator,
  RefreshControl,
  StatusBar,
  Dimensions,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
import { COMMERCE_DISABLED, CommerceUnavailable } from '../../utils/iosCommerce';

const API_BASE = 'https://api.jobeng.ai/api';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const { width } = Dimensions.get('window');

const PaymentScenariosScreen = ({ navigation }) => {
  if (COMMERCE_DISABLED) return <CommerceUnavailable navigation={navigation} />;
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [userType, setUserType] = useState('professional');

  const categories = [
    { id: 'all', name: 'All Plans', icon: 'apps', color: '#0e4e96' },
    { id: 'business', name: 'Business', icon: 'office-building', color: '#0e4e96' },
    { id: 'professional', name: 'Professional', icon: 'account-tie', color: '#7C3AED' },
    { id: 'learning', name: 'Learning', icon: 'school', color: '#059669' },
  ];

  useEffect(() => {
    loadUserType();
    fetchPlans();
  }, []);

  const loadUserType = async () => {
    const type = await AsyncStorage.getItem(`professional_${_profUserId}_user_type`) || 'professional';
    setUserType(type);
  };

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/subscription-plans`, {
        headers: { 'X-API-Key': API_KEY },
      });
      const data = await response.json();
      if (data.success) {
        setPlans(data.data);
      } else {
        setPlans([]);
      }
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchPlans();
    setRefreshing(false);
  };

  const filteredPlans = selectedCategory === 'all'
    ? plans
    : plans.filter(plan => plan.category === selectedCategory);

  const getPlanColors = (category) => {
    switch (category) {
      case 'business': return ['#0e4e96', '#1a6bb0', '#2d7ac0'];
      case 'professional': return ['#7C3AED', '#9D6CFA', '#B794F6'];
      case 'learning': return ['#059669', '#10B981', '#34D399'];
      default: return ['#0e4e96', '#1a6bb0', '#2d7ac0'];
    }
  };

  const getPlanIcon = (category) => {
    switch (category) {
      case 'business': return 'office-building';
      case 'professional': return 'account-tie';
      case 'learning': return 'school';
      default: return 'crown';
    }
  };

  const handleSelectPlan = (plan) => {
    navigation.navigate('PaymentScreen', {
      scenario: {
        id: plan.plan_id,
        type: 'subscription',
        category: plan.category,
        name: plan.name,
        description: `Monthly ${plan.category} subscription`,
        amountInPence: Math.round(parseFloat(plan.price) * 100),
        currency: 'GBP',
        interval: plan.billing_cycle,
        features: plan.features,
        plan_id: plan.plan_id,   // snake_case: PaymentScreen reads scenario.plan_id
        planId: plan.plan_id,    // kept for any camelCase consumers
        max_users: plan.max_users,
      }
    });
  };

  if (loading && !refreshing) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size={48} color="#0e4e96" />
        <Text style={styles.loadingText}>Loading amazing plans...</Text>
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      <LinearGradient
        colors={['#0e4e96', '#1a6bb0']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.header}
      >
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        <View style={styles.headerTextContainer}>
          <Text style={styles.headerTitle}>Choose Your Plan</Text>
          <Text style={styles.headerSubtitle}>Unlock premium features</Text>
        </View>
        <TouchableOpacity style={styles.refreshButton} onPress={onRefresh}>
          <Ionicons name="refresh" size={24} color="#fff" />
        </TouchableOpacity>
      </LinearGradient>

      <View style={styles.categoryContainer}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.categoryScroll}
        >
          {categories.map((cat) => (
            <TouchableOpacity
              key={cat.id}
              style={[
                styles.categoryButton,
                selectedCategory === cat.id && styles.categoryButtonActive,
                { borderColor: cat.color }
              ]}
              onPress={() => setSelectedCategory(cat.id)}
            >
              <MaterialCommunityIcons
                name={cat.icon}
                size={20}
                color={selectedCategory === cat.id ? cat.color : '#64748b'}
              />
              <Text style={[
                styles.categoryText,
                selectedCategory === cat.id && { color: cat.color, fontWeight: '600' }
              ]}>
                {cat.name}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      <ScrollView
        style={styles.content}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        showsVerticalScrollIndicator={false}
      >
        {filteredPlans.length === 0 ? (
          <View style={styles.emptyContainer}>
            <MaterialCommunityIcons name="package-variant" size={64} color="#cbd5e1" />
            <Text style={styles.emptyText}>No plans available</Text>
          </View>
        ) : (
          filteredPlans.map((plan, index) => {
            const colors = getPlanColors(plan.category);
            const icon = getPlanIcon(plan.category);
            const isPopular = plan.plan_id.includes('professional') || plan.plan_id.includes('enterprise');

            return (
              <TouchableOpacity
                key={plan.id}
                style={styles.planCard}
                onPress={() => handleSelectPlan(plan)}
                activeOpacity={0.9}
              >
                {isPopular && (
                  <View style={styles.popularBadge}>
                    <Text style={styles.popularText}>🔥 POPULAR</Text>
                  </View>
                )}

                <LinearGradient
                  colors={colors}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 1 }}
                  style={styles.planGradient}
                >
                  <View style={styles.planIconContainer}>
                    <MaterialCommunityIcons name={icon} size={32} color="#fff" />
                  </View>

                  <View style={styles.planInfo}>
                    <Text style={styles.planName}>{plan.name}</Text>
                    <Text style={styles.planCategory}>{plan.category}</Text>
                  </View>

                  <View style={styles.planPriceContainer}>
                    <Text style={styles.planPrice}>£{parseFloat(plan.price).toFixed(2)}</Text>
                    <Text style={styles.planPeriod}>/{plan.billing_cycle}</Text>
                  </View>
                </LinearGradient>

                <View style={styles.planDetails}>
                  <View style={styles.featuresList}>
                    {plan.features.slice(0, 4).map((feature, idx) => (
                      <View key={idx} style={styles.featureItem}>
                        <Ionicons name="checkmark-circle" size={18} color="#10B981" />
                        <Text style={styles.featureText}>{feature}</Text>
                      </View>
                    ))}
                    {plan.features.length > 4 && (
                      <Text style={styles.moreFeatures}>
                        +{plan.features.length - 4} more features
                      </Text>
                    )}
                  </View>

                  <View style={styles.planFooter}>
                    <View style={styles.userCount}>
                      <Ionicons name="people" size={16} color="#64748b" />
                      <Text style={styles.userCountText}>Up to {plan.max_users === 999 ? 'unlimited' : plan.max_users} users</Text>
                    </View>

                    <TouchableOpacity
                      style={styles.selectButton}
                      onPress={() => handleSelectPlan(plan)}
                    >
                      <Text style={styles.selectButtonText}>Select Plan</Text>
                      <Ionicons name="arrow-forward" size={16} color="#fff" />
                    </TouchableOpacity>
                  </View>
                </View>
              </TouchableOpacity>
            );
          })
        )}
        <View style={styles.bottomPadding} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748b',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 20,
    borderBottomLeftRadius: 30,
    borderBottomRightRadius: 30,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTextContainer: {
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#fff',
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.8)',
    marginTop: 4,
  },
  refreshButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoryContainer: {
    backgroundColor: '#fff',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  categoryScroll: {
    paddingHorizontal: 20,
    gap: 12,
  },
  categoryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 25,
    backgroundColor: '#f1f5f9',
    borderWidth: 1,
    borderColor: 'transparent',
    gap: 6,
  },
  categoryButtonActive: {
    backgroundColor: '#fff',
    borderWidth: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  categoryText: {
    fontSize: 14,
    color: '#475569',
  },
  content: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  planCard: {
    backgroundColor: '#fff',
    borderRadius: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
    position: 'relative',
  },
  popularBadge: {
    position: 'absolute',
    top: -10,
    right: 20,
    backgroundColor: '#FFD700',
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 20,
    zIndex: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 3,
  },
  popularText: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#1e293b',
  },
  planGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
  },
  planIconContainer: {
    width: 50,
    height: 50,
    borderRadius: 25,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  planInfo: {
    flex: 1,
  },
  planName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#fff',
    marginBottom: 4,
  },
  planCategory: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.9)',
    textTransform: 'capitalize',
  },
  planPriceContainer: {
    alignItems: 'flex-end',
  },
  planPrice: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#fff',
  },
  planPeriod: {
    fontSize: 14,
    color: 'rgba(255,255,255,0.8)',
  },
  planDetails: {
    padding: 20,
  },
  featuresList: {
    marginBottom: 16,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  featureText: {
    fontSize: 14,
    color: '#334155',
    marginLeft: 10,
    flex: 1,
  },
  moreFeatures: {
    fontSize: 12,
    color: '#64748b',
    fontStyle: 'italic',
    marginTop: 4,
  },
  planFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 16,
  },
  userCount: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  userCountText: {
    fontSize: 14,
    color: '#64748b',
  },
  selectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0e4e96',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 25,
    gap: 6,
  },
  selectButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#fff',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 16,
    color: '#94a3b8',
    marginTop: 16,
  },
  bottomPadding: {
    height: 40,
  },
});

export default PaymentScenariosScreen;